'use client'
import { useEffect, useRef, useState } from "react";
import styles from "@/components/Nav/nav.module.css";
import Link from "next/link";
import Image from "next/image";
import CurrencyChooser from "@/components/CurrencyChooser/CurrencyChooser";

/**
 * Whatever actually scrolls above this element. globals.css gives
 * `.light-theme/.dark-theme` `height:100vh; overflow:auto`, so the WINDOW never
 * scrolls on this site — the page scrolls inside that wrapper. Walk up to find
 * it (and ignore unrelated scrollers like the search page's filter rail).
 */
function scrollParent(el) {
    for (let p = el?.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        // Deliberately NOT also testing `scrollHeight > clientHeight`: on pages
        // that render short first and grow (the booking page, while the car
        // loads) nothing overflowed yet at mount, so that test fell through to
        // `window` — which never scrolls here — and the header stopped
        // collapsing entirely.
        if (oy === "auto" || oy === "scroll") return p;
    }
    return window;
}

export default function Nav() {
    const [menuOpen, setMenuOpen] = useState(false);
    const headerRef = useRef(null);
    const scrollerRef = useRef(null);

    // The header always shows its compact form (the bar that used to appear once
    // the page moved): it no longer grows back at the top of the page — on the
    // owner's request, 2026-10-19. The scroller is still found for the menu lock.
    useEffect(() => {
        scrollerRef.current = scrollParent(headerRef.current);
    }, []);

    // The slide-out shouldn't scroll the page behind it — lock the real
    // scroller, not <body> (which isn't the one that moves here).
    useEffect(() => {
        if (!menuOpen) return;
        const scroller = scrollerRef.current;
        const node = scroller && scroller !== window ? scroller : document.body;
        const prev = node.style.overflow;
        node.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
        document.addEventListener("keydown", onKey);
        return () => {
            node.style.overflow = prev;
            document.removeEventListener("keydown", onKey);
        };
    }, [menuOpen]);

    return (
        <>
        <header ref={headerRef} className={`${styles['nav-wrapper']} ${styles.scrolled}`}>
            <div className={styles.nav}>
                <a href="/" aria-label="Home" className={styles.logo}>
                    <Image
                        src="/assets/images/logo-light.svg"
                        width={230}
                        height={58}
                        alt="PaRent car rental"
                        priority
                    />
                </a>
                <nav className={styles.menu}>
                    <Link href="/terms">TERMS</Link>
                    <Link href="/#about-us">ABOUT US</Link>
                    <Link href="/#contact-form">CONTACT</Link>
                </nav>
                <div className={styles['right-nav']}>
                    <CurrencyChooser />
                </div>
                <button
                    type="button"
                    className={styles.bars}
                    onClick={() => setMenuOpen(true)}
                    aria-label="Open menu"
                    aria-expanded={menuOpen}
                >
                    <svg
                        stroke="currentColor"
                        fill="none"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        height="30px"
                        width="30px"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                        />
                    </svg>
                </button>
            </div>
        </header>

        {/* Deliberately OUTSIDE <header>: the bar has a backdrop-filter, which
            makes it the containing block for position:fixed children — the
            slide-out then anchors to the bar instead of the viewport and its
            edge peeked onto the desktop layout. */}
        <div
                className={`${styles['mobile-menu']} ${menuOpen ? styles.open : ''
                    }`}
            >
                <div className={styles['mobile-menu-header']}>
                    <Link href="/" aria-label="Home">
                        <Image
                            src="/assets/images/logo-light.svg"
                            width={230}
                            height={58}
                            alt="PaRent car rental"
                        />
                    </Link>
                    <button
                        type="button"
                        className={styles.close}
                        onClick={() => setMenuOpen(false)}
                        aria-label="Close menu"
                    >
                        &times;
                    </button>
                </div>
                <nav className={styles['mobile-menu-links']}>
                    <Link href="/terms" onClick={() => setMenuOpen(false)}>
                        TERMS
                    </Link>
                    <Link href="/#about-us" onClick={() => setMenuOpen(false)}>
                        ABOUT US
                    </Link>
                    <Link href="/#contact-form" onClick={() => setMenuOpen(false)}>
                        CONTACT
                    </Link>
                </nav>
            </div>

            {menuOpen && (
                <div
                    className={styles.overlay}
                    onClick={() => setMenuOpen(false)}
                />
            )}
        </>
    );
}
